// /auth?token=… is where the WhatsApp magic link lands. The token is swapped
// for a session by a script-driven POST, never on page load by the server, so
// WhatsApp's link-preview fetch can't use up the one-time token.
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@cover/core/ui/States.tsx';
import { Card, CardBody } from '@cover/core/ui/Card.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { useBrand, whatsappLink } from '@cover/core/brand.tsx';

export function AuthPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const brand = useBrand();
  const [failed, setFailed] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = params.get('token');
    // Drop the token from the address bar and history straight away.
    window.history.replaceState(null, '', '/auth');
    if (!token) {
      setFailed('This link is missing its code.');
      return;
    }
    fetch('/session/exchange', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) })
      .then(async (res) => {
        const body = (await res.json()) as { redirect?: string; error?: { message: string } };
        if (!res.ok) throw new Error(body.error?.message ?? 'Login failed');
        await qc.invalidateQueries();
        navigate(body.redirect ?? '/', { replace: true });
      })
      .catch((e: Error) => setFailed(e.message));
  }, [params, navigate, qc]);

  if (!failed) return <Spinner label="Signing you in" />;
  const wa = whatsappLink(brand, 'Can you send me a new dashboard link?');
  return (
    <main className="mx-auto max-w-md px-4 py-10">
      <h1 className="mb-4 text-2xl font-bold">That link didn’t work</h1>
      <Card>
        <CardBody className="space-y-4">
          <p>{failed} Links work once and only for 15 minutes.</p>
          {wa && (
            <Button asChild size="full">
              <a href={wa} className="text-on-brand no-underline hover:no-underline">
                Ask for a new link in WhatsApp
              </a>
            </Button>
          )}
          <p className="text-sm text-mid">
            Or <Link to="/login">sign in with your email</Link>.
          </p>
        </CardBody>
      </Card>
    </main>
  );
}
